// Simple API cache using in-memory Map + sessionStorage with TTL
// Usage:
//   import { getOrFetch } from '../utils/apiCache';
//   const data = await getOrFetch(key, () => axios.get(url).then(r => r.data), { ttlMs: 10 * 60 * 1000 })

const memoryCache = new Map(); // key -> { value, expiresAt }

const now = () => Date.now();

function makeStorageKey(key) {
  return `apicache:${key}`;
}

export function getCached(key) {
  // 1) Try memory cache
  const mem = memoryCache.get(key);
  if (mem && mem.expiresAt > now()) {
    return mem.value;
  }

  // 2) Try sessionStorage
  try {
    const raw = sessionStorage.getItem(makeStorageKey(key));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.expiresAt && parsed.expiresAt > now()) {
        // rehydrate memory for faster access next time
        memoryCache.set(key, { value: parsed.value, expiresAt: parsed.expiresAt });
        return parsed.value;
      } else {
        sessionStorage.removeItem(makeStorageKey(key));
      }
    }
  } catch (_e) {
    // ignore storage errors
  }

  return undefined;
}

export function setCached(key, value, ttlMs = 5 * 60 * 1000) {
  const expiresAt = now() + ttlMs;
  memoryCache.set(key, { value, expiresAt });
  try {
    sessionStorage.setItem(
      makeStorageKey(key),
      JSON.stringify({ value, expiresAt })
    );
  } catch (_e) {
    // ignore storage errors
  }
}

export async function getOrFetch(key, fetcher, options = {}) {
  const ttlMs = options.ttlMs ?? 5 * 60 * 1000;

  const cached = getCached(key);
  if (cached !== undefined) return cached;

  const value = await fetcher();
  setCached(key, value, ttlMs);
  return value;
}
